import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "bootstrap-4-react/lib/components";
import LoaderSpinner from '../common/Loader';
import { toast } from "react-toastify";
import moment from 'moment';
import { checkWalmartFeedStatus } from '../../services/walmart.service';

const WalmartFeedDetail = () => {
    const { feedId } = useParams();
    const navigate = useNavigate();
    const [feedStatus, setFeedStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    const userDetails = useSelector((state) => state.auth.user);
    const companyList = useSelector((state) => state.auth.companyList);

    const companySellerId = useMemo(() => {
        const currentCompany = JSON.parse(localStorage.getItem('currentCompany') || '{}');
        if (currentCompany?.companySellerId) return currentCompany.companySellerId;
        if (userDetails?.Value?.companySellerId && userDetails.Value.companySellerId !== 0) {
            return userDetails.Value.companySellerId;
        }
        if (companyList?.length > 0) return companyList[0].companySellerId;
        return null;
    }, [userDetails, companyList]);

    const fetchFeedDetail = async () => {
        if (!companySellerId || !feedId) return;
        setLoading(true);
        const statusResult = await checkWalmartFeedStatus(feedId, companySellerId);
        if (statusResult?.error) {
            toast.error(statusResult.message || 'Failed to fetch feed details');
        } else if (statusResult) {
            setFeedStatus(statusResult);
        } else {
            toast.error('Failed to fetch feed details');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchFeedDetail();
    }, [feedId, companySellerId]);

    // --- Error classification helpers ---

    /** Truncated product type names like "Skateboar" — not real errors, hide them */
    const isRequiredAttributeError = (err) => {
        const desc = (err.description || '').toLowerCase();
        if (!desc.includes('is a required attribute')) return false;
        if (err.field && isRealFieldName(err.field)) return false;
        return true;
    };

    const isRealFieldName = (field) => {
        if (!field) return false;
        if (field.length > 80) return false;
        if (field.includes('(') || field.includes(')')) return false;
        if (field.includes(' ')) return false;
        return true;
    };

    // --- Rendering helpers ---

    const FIELD_LABELS = {
        productIdentifiers: 'Product ID',
        productName: 'Product Name',
        shortDescription: 'Short Description',
        mainImageUrl: 'Main Image URL',
        brand: 'Brand',
        fabricContent: 'Fabric Content',
        material: 'Material',
        color: 'Color',
        colorCategory: 'Color Category',
        price: 'Price',
        ShippingWeight: 'Shipping Weight',
        shippingWeight: 'Shipping Weight',
        specProductType: 'Product Type',
        country_of_origin_substantial_transformation: 'Country of Origin',
        keyFeatures: 'Key Features',
        assembledProductLength: 'Length',
        assembledProductWidth: 'Width',
        assembledProductHeight: 'Height',
        assembledProductWeight: 'Weight',
        condition: 'Condition',
        gender: 'Gender',
        powerType: 'Power Type',
        smallPartsWarnings: 'Small Parts Warning',
        isProp65WarningRequired: 'Prop 65 Warning',
    };

    const friendlyFieldName = (field) => FIELD_LABELS[field] || field.replaceAll(/([A-Z])/g, ' $1').replaceAll('_', ' ').replace(/^\w/, c => c.toUpperCase()).trim();

    const simplifyError = (description) => {
        if (!description) return '';
        let msg = description.replace(/\.\s*For more information,?\s*search.*$/i, '.');
        msg = msg.trim();
        if (msg && !msg.endsWith('.')) msg += '.';
        return msg;
    };

    const getStatusBadge = (status) => {
        if (!status) return <span className="activeStatus">Unknown</span>;
        const statusMap = {
            'SUCCESS': { cls: 'activeStatus', label: 'Success' },
            'PROCESSED': { cls: 'activeStatus', label: 'Processed' },
            'INPROGRESS': { cls: 'activeStatus', label: 'Processing' },
            'ERROR': { cls: 'errorStatus', label: 'Error' },
            'DATA_ERROR': { cls: 'errorStatus', label: 'Error' },
            'SYSTEM_ERROR': { cls: 'errorStatus', label: 'System Error' },
            'TIMEOUT_ERROR': { cls: 'errorStatus', label: 'Timeout' },
        };
        const mapped = statusMap[status];
        if (mapped) return <span className={mapped.cls}>{mapped.label}</span>;
        return <span className="activeStatus">{status}</span>;
    };

    const formatEpoch = (epoch) => {
        if (!epoch) return '-';
        return moment(epoch).format("MM-DD-YYYY h:mm A");
    };

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec mb-1">
                    <div>Feed Details</div>
                    <div className="top_right_button_container">
                        <Button
                            className="cus-seconday-bg-btn top-btn"
                            onClick={fetchFeedDetail}
                        >
                            <i className="fa fa-refresh" aria-hidden="true"></i> Refresh
                        </Button>
                        <button
                            className="btn cus-primary-transparent-btn top-btn"
                            onClick={() => navigate('/walmart-feeds')}
                        >
                            Back
                        </button>
                    </div>
                </div>

                {loading && (
                    <div className="full-screen-spinner-overlay"><LoaderSpinner /></div>
                )}

                {feedStatus && (
                    <div className="main_content cus-page-content-sec">
                        {/* Feed summary */}
                        <div className="row mb-3 p-3">
                            <div className="col-md-6">
                                <div className="row modal_input mb-1">
                                    <div className="col-sm-5 formLabel">
                                        <span className="input-label fnt-wgt-editmgmt">Status:</span>
                                    </div>
                                    <div className="col-sm-7">
                                        {getStatusBadge(feedStatus.feedStatus)}
                                    </div>
                                </div>
                                <div className="row modal_input mb-1">
                                    <div className="col-sm-5 formLabel">
                                        <span className="input-label fnt-wgt-editmgmt">Submitted:</span>
                                    </div>
                                    <div className="col-sm-7">
                                        {formatEpoch(feedStatus.feedSubmissionDate)}
                                    </div>
                                </div>
                                <div className="row modal_input mb-1">
                                    <div className="col-sm-5 formLabel">
                                        <span className="input-label fnt-wgt-editmgmt">Feed ID:</span>
                                    </div>
                                    <div className="col-sm-7" style={{ fontSize: '11px', color: '#6c757d', wordBreak: 'break-all' }}>
                                        {feedStatus.feedId}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="row modal_input mb-1">
                                    <div className="col-sm-5 formLabel">
                                        <span className="input-label fnt-wgt-editmgmt">Total Items:</span>
                                    </div>
                                    <div className="col-sm-7">{feedStatus.itemsReceived || 0}</div>
                                </div>
                                <div className="row modal_input mb-1">
                                    <div className="col-sm-5 formLabel">
                                        <span className="input-label fnt-wgt-editmgmt">Succeeded:</span>
                                    </div>
                                    <div className="col-sm-7">
                                        <span className="activeStatus" style={{ display: 'inline-block' }}>{feedStatus.itemsSucceeded || 0}</span>
                                    </div>
                                </div>
                                <div className="row modal_input mb-1">
                                    <div className="col-sm-5 formLabel">
                                        <span className="input-label fnt-wgt-editmgmt">Failed:</span>
                                    </div>
                                    <div className="col-sm-7">
                                        {(feedStatus.itemsFailed || 0) > 0
                                            ? <span className="errorStatus" style={{ display: 'inline-block' }}>{feedStatus.itemsFailed}</span>
                                            : <span>0</span>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {feedStatus.itemsProcessing > 0 && (
                            <div className="alert alert-info mx-3">
                                {feedStatus.itemsProcessing} item(s) still processing. Check again in a few minutes.
                            </div>
                        )}

                        {/* Item-level details */}
                        {feedStatus.itemDetails?.itemIngestionStatus?.map((item, idx) => {
                            const hasErrors = item.ingestionErrors?.ingestionError?.length > 0;
                            const isSuccess = item.ingestionStatus === 'SUCCESS';

                            return (
                                <div key={item.sku || idx} className="border rounded p-3 mb-3 mx-3">
                                    {/* Item header */}
                                    <div className="d-flex align-items-center mb-2">
                                        <span className="input-label fnt-wgt-editmgmt mr-2">SKU: {item.sku}</span>
                                        <span className="ml-2">{getStatusBadge(item.ingestionStatus)}</span>
                                    </div>

                                    {/* Success message */}
                                    {isSuccess && (
                                        <div className="alert alert-success py-2 mb-0">
                                            <i className="fa fa-check-circle mr-1" aria-hidden="true"></i>{' '}
                                            Item published successfully to Walmart.
                                        </div>
                                    )}

                                    {/* Error list */}
                                    {hasErrors && (
                                        <div className="mt-2">
                                            {(() => {
                                                const displayErrors = item.ingestionErrors.ingestionError
                                                    .filter(err => !isRequiredAttributeError(err));
                                                return (
                                                    <>
                                                        <span className="input-label fnt-wgt-editmgmt mb-2" style={{ display: 'block' }}>
                                                            Issues ({displayErrors.length})
                                                        </span>
                                                        {displayErrors.map((err) => {
                                                            const fieldLabel = isRealFieldName(err.field)
                                                                ? friendlyFieldName(err.field)
                                                                : null;
                                                            return (
                                                                <div key={`${err.field}-${err.code}`} className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: '13px' }}>
                                                                    {fieldLabel && (
                                                                        <strong className="mr-1">{fieldLabel}:</strong>
                                                                    )}
                                                                    {simplifyError(err.description || err.field)}
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </main>
    );
};

export default WalmartFeedDetail;
